import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  GitFork,
  Plus,
  Trash,
  PencilSimple,
  CircleNotch,
  ChatCircleDots,
  PlayCircle,
  PauseCircle,
  Archive,
  Question,
  UserPlus,
} from "@phosphor-icons/react";

import { useCan } from "@/hooks/use-can";
import { apiClient } from "@dukene/shared";
import { Button } from "@/components/ui/button";
import { GatedButton } from "@/components/ui/gated-button";
import { confirmAction } from "../../components/modals/ConfirmModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface FlowRow {
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "archived";
  trigger_type: "keyword" | "first_inbound_message" | "manual";
  trigger_config: { keywords?: string[] } | Record<string, unknown>;
  execution_count: number;
  last_executed_at: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABELS: Record<FlowRow["status"], string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

const STATUS_COLORS: Record<FlowRow["status"], string> = {
  draft: "bg-[#E1E4E8] text-[#5C5F62] border-[#C9CCCF]",
  active: "bg-[#E3F1DF] text-[#006838] border-[#B3DEC1]",
  archived: "bg-[#F6F6F7] text-[#5C5F62] border-[#D2D5D9]",
};

interface TemplateSummary {
  slug: string;
  name: string;
  description: string;
  icon: "MessageSquare" | "HelpCircle" | "UserPlus";
  trigger_type: string;
  node_count: number;
}

const TEMPLATE_ICONS = {
  MessageSquare: ChatCircleDots,
  HelpCircle: Question,
  UserPlus: UserPlus,
} as const;

export default function FlowsPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const canCreate = useCan("send-messages");
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [flowsRes, tmplRes]: any = await Promise.all([
          apiClient.get("/whatsapp/bot-flows"),
          apiClient.get("/whatsapp/bot-flows/templates").catch(() => ({ templates: [] }))
        ]);
        const flowsJson = flowsRes as { flows: FlowRow[] };
        if (!cancelled) setFlows(flowsJson.flows ?? []);
        if (tmplRes) {
          const tmplJson = tmplRes as { templates: TemplateSummary[] };
          if (!cancelled) setTemplates(tmplJson.templates ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          toast.error("Couldn't load flows.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res: any = await apiClient.post("/whatsapp/bot-flows", {
        name: newName.trim(),
        trigger_type: "keyword",
        trigger_config: { keywords: [] },
      });
      const json = res as { flow: FlowRow };
      setCreateOpen(false);
      setNewName("");
      navigate(`/whatsapp/flows/${json.flow.id}`);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't create flow.");
    } finally {
      setCreating(false);
    }
  }

  async function handleUseTemplate(slug: string) {
    setCreating(true);
    try {
      const res: any = await apiClient.post("/whatsapp/bot-flows", { template_slug: slug });
      const json = res as { flow: FlowRow };
      setCreateOpen(false);
      navigate(`/whatsapp/flows/${json.flow.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Clone failed";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(flow: FlowRow) {
    const yes = await confirmAction({
      title: "Delete Flow",
      message: `Delete "${flow.name}"? Any active runs will end immediately.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!yes) return;
    try {
      await apiClient.delete(`/whatsapp/bot-flows/${flow.id}`);
      setFlows((prev) => prev.filter((f) => f.id !== flow.id));
      toast.success("Flow deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't delete flow.");
    }
  }

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <CircleNotch weight="bold" className="h-6 w-6 animate-spin text-[#5C5F62]" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", embedded ? "p-0" : "p-6")}>
      <header className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold text-[#202223] dark:text-white">WhatsApp Bot Flows</h1>
            <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
              Automated Assistant
            </span>
          </div>
          <p className="mt-0.5 text-xs text-[#5C5F62] dark:text-slate-400">
            Build branching WhatsApp conversations for customer FAQs, welcome menus, and automated order lookups
          </p>
        </div>
        <GatedButton
          canAct={canCreate}
          gateReason="create flows"
          onClick={() => setCreateOpen(true)}
          className="h-8 px-3.5 text-xs font-semibold bg-[#006838] hover:bg-[#00522c] text-white rounded-lg inline-flex items-center gap-1.5"
        >
          <Plus weight="bold" className="h-3.5 w-3.5" />
          Create New Flow
        </GatedButton>
      </header>

      {flows.length === 0 ? (
        <EmptyState
          onCreate={() => setCreateOpen(true)}
          canCreate={canCreate}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {flows.map((flow) => (
            <FlowCard
              key={flow.id}
              flow={flow}
              onEdit={() => navigate(`/whatsapp/flows/${flow.id}`)}
              onDelete={() => handleDelete(flow)}
            />
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-2xl bg-white dark:bg-[#1A1D21]">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-[#202223] dark:text-white">
              Create a Bot Flow
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C5F62]">
              Start with a battle-tested template or build a custom conversation tree from scratch.
            </DialogDescription>
          </DialogHeader>

          {templates.length > 0 && (
            <div className="space-y-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5C5F62]">
                Start from a template
              </p>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {templates.map((tmpl) => {
                  const IconComp =
                    TEMPLATE_ICONS[tmpl.icon as keyof typeof TEMPLATE_ICONS] ??
                    ChatCircleDots;
                  return (
                    <button
                      key={tmpl.slug}
                      type="button"
                      disabled={creating}
                      onClick={() => handleUseTemplate(tmpl.slug)}
                      className="flex flex-col items-start rounded-xl border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 p-3 text-left transition-all hover:border-[#006838] hover:bg-[#F6F6F7] dark:hover:bg-slate-800 disabled:opacity-50"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E3F1DF] text-[#006838]">
                        <IconComp weight="fill" className="h-4 w-4" />
                      </div>
                      <h4 className="mt-2 text-xs font-semibold text-[#202223] dark:text-white">
                        {tmpl.name}
                      </h4>
                      <p className="mt-1 line-clamp-2 text-[11px] text-[#5C5F62] dark:text-slate-400">
                        {tmpl.description}
                      </p>
                      <span className="mt-2 text-[10px] font-semibold text-[#006838]">
                        Use template →
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2 border-t border-[#D2D5D9] dark:border-slate-800 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5C5F62]">
              Or start blank
            </p>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Welcome menu"
              className="h-8 text-xs bg-[#F6F6F7] dark:bg-slate-900 border-[#D2D5D9]"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              className="text-xs font-medium"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="text-xs font-semibold bg-[#006838] hover:bg-[#00522c] text-white"
            >
              {creating && <CircleNotch weight="bold" className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Create Blank Flow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({
  onCreate,
  canCreate,
}: {
  onCreate: () => void;
  canCreate: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-[#1A1D21] px-6 py-14 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E3F1DF] text-[#006838]">
        <GitFork weight="fill" className="h-6 w-6" />
      </div>
      <h2 className="mt-4 text-sm font-semibold text-[#202223] dark:text-white">
        No bot flows yet
      </h2>
      <p className="mt-1 max-w-md text-xs text-[#5C5F62] dark:text-slate-400">
        Build your first WhatsApp conversation flow — a welcome menu, order lookup, or customer service triage. Customers interact with structured buttons that guide them to answers automatically.
      </p>
      <GatedButton
        canAct={canCreate}
        gateReason="create flows"
        onClick={onCreate}
        className="mt-4 h-8 px-4 text-xs font-semibold bg-[#006838] hover:bg-[#00522c] text-white rounded-lg inline-flex items-center gap-1.5"
      >
        <Plus weight="bold" className="h-3.5 w-3.5" />
        Create Your First Flow
      </GatedButton>
    </div>
  );
}

function FlowCard({
  flow,
  onEdit,
  onDelete,
}: {
  flow: FlowRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const triggerSummary = describeTrigger(flow);
  const StatusIcon =
    flow.status === "active"
      ? PlayCircle
      : flow.status === "archived"
        ? Archive
        : PauseCircle;
  return (
    <div className="flex flex-col rounded-xl border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-[#1A1D21] p-4 transition-all hover:border-[#006838] shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <GitFork weight="fill" className="h-4 w-4 shrink-0 text-[#006838]" />
          <h3 className="truncate text-xs font-semibold text-[#202223] dark:text-white">
            {flow.name}
          </h3>
        </div>
        <Badge
          variant="outline"
          className={cn(
            "shrink-0 gap-1 text-[10px] font-semibold border px-2 py-0.5",
            STATUS_COLORS[flow.status],
          )}
        >
          <StatusIcon weight="fill" className="h-3 w-3" />
          {STATUS_LABELS[flow.status]}
        </Badge>
      </div>

      <p className="mt-2 line-clamp-2 text-xs text-[#5C5F62] dark:text-slate-400">
        {flow.description || triggerSummary}
      </p>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-[#5C5F62] dark:text-slate-400">
        <span className="inline-flex items-center gap-1 font-medium">
          <ChatCircleDots weight="fill" className="h-3 w-3 text-[#006838]" />
          {flow.execution_count} {flow.execution_count === 1 ? "execution" : "executions"}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#E1E4E8] dark:border-slate-800 pt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="h-7 px-2.5 text-xs font-medium text-[#202223] dark:text-slate-200 hover:bg-[#F6F6F7]"
        >
          <PencilSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
          Edit Flow
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="h-7 px-2.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
        >
          <Trash weight="bold" className="h-3.5 w-3.5 mr-1" />
          Delete
        </Button>
      </div>
    </div>
  );
}

function describeTrigger(flow: FlowRow): string {
  if (flow.trigger_type === "keyword") {
    const keywords = Array.isArray(flow.trigger_config.keywords)
      ? (flow.trigger_config.keywords as string[])
      : [];
    if (keywords.length === 0) return "Triggers on keyword match (none configured)";
    return `Triggers on keywords: ${keywords.join(", ")}`;
  }
  if (flow.trigger_type === "first_inbound_message") {
    return "Triggers on contact's first inbound message";
  }
  return "Manual execution trigger";
}
